import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import type { Set, YearGroup } from '../../types';

const setSchema = z.object({
  name: z.string().min(1, 'Set name is required'),
  subjectId: z.string().min(1, 'Subject is required'),
  yearGroupId: z.string().min(1, 'Year group is required'),
  targetAbilityLevel: z.enum(['high', 'medium', 'low']),
  capacity: z.coerce.number().min(1, 'Capacity must be at least 1'),
});

type SetFormData = z.infer<typeof setSchema>;

interface SetFormProps {
  set?: Set;
  yearGroups: YearGroup[];
  onSubmit: (data: Set) => void;
  onCancel: () => void;
}

const abilityLevels: { value: 'high' | 'medium' | 'low'; label: string }[] = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

export const SetForm: React.FC<SetFormProps> = ({ set, yearGroups, onSubmit, onCancel }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SetFormData>({
    resolver: zodResolver(setSchema),
    defaultValues: {
      name: set?.name || '',
      subjectId: set?.subjectId || 'subject-math',
      yearGroupId: set?.yearGroupId || '',
      targetAbilityLevel: set?.targetAbilityLevel || 'medium',
      capacity: set?.capacity || 25,
    },
  });

  const handleFormSubmit = (data: SetFormData) => {
    onSubmit({
      ...data,
      id: set?.id || `set-${Date.now()}`,
      currentSize: set?.currentSize || 0,
    } as Set);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Set Name"
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.message || 'e.g., Year 7 Maths Set 1'}
                required
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="subjectId"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Subject ID"
                fullWidth
                error={!!errors.subjectId}
                helperText={errors.subjectId?.message || 'e.g., subject-math'}
                required
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="yearGroupId"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.yearGroupId}>
                <InputLabel>Year Group *</InputLabel>
                <Select {...field} label="Year Group *">
                  {yearGroups.map((yg) => (
                    <MenuItem key={yg.id} value={yg.id}>
                      {yg.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.yearGroupId && <FormHelperText>{errors.yearGroupId.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="targetAbilityLevel"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.targetAbilityLevel}>
                <InputLabel>Ability Level *</InputLabel>
                <Select {...field} label="Ability Level *">
                  {abilityLevels.map((level) => (
                    <MenuItem key={level.value} value={level.value}>
                      {level.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.targetAbilityLevel && <FormHelperText>{errors.targetAbilityLevel.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="capacity"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Capacity"
                type="number"
                fullWidth
                error={!!errors.capacity}
                helperText={errors.capacity?.message}
                required
              />
            )}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="contained">
          {set ? 'Update' : 'Create'} Set
        </Button>
      </Box>
    </form>
  );
};
